import { useState, useEffect, useMemo } from 'react';
import api from '../api';
import { useBack } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { ExtraStaffEditor } from '../components/ExtraStaff';

// Staffing: how many people are available for moulding, and how much of a
// person each part needs (designed with the director 22 Sep 2026).
//
// Capacity, not people: nothing here asks who anyone is. The numbers are
// ranges because they honestly are -- students come for four to eight hours --
// and the one calculation downstream is the same whether a range is wide or a
// single figure. Every change is recorded in the history.

const DARK = '#0f1117', CARD = '#1e2329', SUNK = '#131920', BORD = '#2a3040';
const MUTED = '#6b7280', DIM = '#9ca3af', TEXT = '#f0f2f5', TEAL = '#14b8a6', AMBER = '#f59e0b', RED = '#fca5a5';
const SHIFT = { 1: 'Morning', 2: 'Afternoon', 3: 'Night' };
const LEANS = [
  ['cautious',   'Cautious',   'Warn early: counts on the fewest people you gave'],
  ['balanced',   'Balanced',   'The middle of your ranges'],
  ['optimistic', 'Optimistic', 'Warn late: counts on the most people you gave'],
];

const st = {
  card:  { border: `1px solid ${BORD}`, borderRadius: 14, overflow: 'hidden', marginBottom: 16, background: CARD },
  head:  { padding: '14px 18px', borderBottom: `1px solid ${BORD}`, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 },
  eyebrow: { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em' },
  row:   { padding: '14px 18px', borderBottom: `1px solid ${BORD}` },
  label: { fontSize: 14, fontWeight: 600, marginBottom: 2 },
  hint:  { fontSize: 12, color: MUTED, lineHeight: 1.5 },
  input: { background: SUNK, border: `1px solid ${BORD}`, borderRadius: 8, color: TEXT, padding: '7px 9px', fontSize: 13, outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box' },
  btn:   { padding: '8px 16px', fontSize: 13, fontWeight: 600, border: `1px solid ${TEAL}`, borderRadius: 8, background: 'rgba(20,184,166,0.1)', color: TEAL, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' },
  ghost: { padding: '6px 11px', fontSize: 12, fontWeight: 600, border: `1px solid ${BORD}`, borderRadius: 7, background: 'transparent', color: DIM, cursor: 'pointer', fontFamily: 'system-ui', whiteSpace: 'nowrap' },
};

function Num({ value, onChange, disabled, width = 54, step = 1, placeholder }) {
  return (
    <input type="number" min="0" step={step} value={value ?? ''} disabled={disabled} placeholder={placeholder}
      onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      style={{ ...st.input, width, textAlign: 'center' }} />
  );
}

export default function Staffing() {
  const goBack = useBack('/admin');
  const { isDirector } = useAuth();
  const canEdit = isDirector();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [week, setWeek] = useState(null);         // { shifts, lean, mould_changes_per_day }
  const [weekState, setWeekState] = useState(null);
  const [levels, setLevels] = useState({});       // part id -> level ('' = not estimated)
  const [collectors, setCollectors] = useState({}); // part id -> collector box pcs ('' = not known)
  const [levelState, setLevelState] = useState(null);
  const [filter, setFilter] = useState('');

  function load() {
    api.get('/api/v1/staffing')
      .then(r => {
        setData(r.data);
        setWeek({ shifts: r.data.shifts, lean: r.data.lean, mould_changes_per_day: r.data.mould_changes_per_day });
        setLevels(Object.fromEntries(r.data.parts.map(p => [p.id, p.attention_level ?? ''])));
        setCollectors(Object.fromEntries(r.data.parts.map(p => [p.id, p.collector_box_qty ?? ''])));
      })
      .catch(e => setError(e.response?.data?.error || 'Staffing could not be read just now.'));
  }
  useEffect(load, []);

  const setShift = (s, k, v) => { setWeek(w => ({ ...w, shifts: { ...w.shifts, [s]: { ...w.shifts[s], [k]: v } } })); setWeekState(null); };

  function saveWeek() {
    setWeekState('saving');
    api.put('/api/v1/staffing', week)
      .then(() => { setWeekState('saved'); load(); })
      .catch(e => setWeekState(e.response?.data?.error || 'Could not save.'));
  }


  const parts = useMemo(() => (data?.parts || []).filter(p =>
    !filter || `${p.name} ${p.part_number || ''}`.toLowerCase().includes(filter.toLowerCase())), [data, filter]);
  const levelDiffers = p => String(levels[p.id] ?? '') !== String(p.attention_level ?? '');
  const collectorDiffers = p => String(collectors[p.id] ?? '') !== String(p.collector_box_qty ?? '');
  const changedLevels = (data?.parts || []).filter(p => levelDiffers(p) || collectorDiffers(p));
  const unset = (data?.parts || []).filter(p => p.attention_level == null);
  // A part without the numbers to estimate from has no suggestion, only a reason.
  const suggestible = unset.filter(p => p.suggestion.level != null);

  function acceptSuggestions() {
    setLevels(l => {
      const next = { ...l };
      for (const p of suggestible) if (next[p.id] === '') next[p.id] = p.suggestion.level;
      return next;
    });
    setLevelState(null);
  }
  function saveLevels() {
    setLevelState('saving');
    api.put('/api/v1/staffing/parts/attention', {
      // Only what changed on each part is sent, so a level saved here never
      // overwrites a collector box someone changed on the part form, or back.
      levels: changedLevels.map(p => ({
        id: p.id,
        ...(levelDiffers(p) ? { level: levels[p.id] === '' ? null : Number(levels[p.id]) } : {}),
        ...(collectorDiffers(p) ? { collector_box_qty: collectors[p.id] === '' ? null : Number(collectors[p.id]) } : {}),
      })),
    })
      .then(r => { setLevelState(`Saved ${r.data.changed} part${r.data.changed === 1 ? '' : 's'}`); load(); })
      .catch(e => setLevelState(e.response?.data?.error || 'Could not save the levels.'));
  }

  if (error) return <Page goBack={goBack}><div style={{ ...st.card, padding: 18, color: RED }}>{error}</div></Page>;
  if (!data || !week) return <Page goBack={goBack}><div style={{ color: MUTED, fontSize: 13 }}>Reading staffing…</div></Page>;

  const hours = data.shift_hours;
  const lvl = data.levels;

  return (
    <Page goBack={goBack}>
      {!data.configured.length && (
        <div style={{ ...st.card, padding: '14px 18px', borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)' }}>
          <div style={{ fontSize: 13, color: '#fcd34d', lineHeight: 1.55 }}>
            Nothing is checked yet. Enter the people you usually have for moulding below, and the board starts telling you when a shift has more work than people.
          </div>
        </div>
      )}

      {/* ── the usual week ── */}
      <div style={st.card}>
        <div style={st.head}>
          <span style={st.eyebrow}><i className="fa-solid fa-people-group" style={{ marginRight: 6 }} />Usual week</span>
          <span style={{ fontSize: 11.5, color: MUTED }}>1 person = {hours} h a shift</span>
        </div>
        <div style={{ ...st.row, ...st.hint }}>
          People <b style={{ color: DIM }}>available for moulding</b> — not everyone in the building. The same people assemble and pack, so count the hours they really have at the machines. A range is fine: students who come for four to eight hours make a morning "3 to 4", not a number.
        </div>
        {[1, 2, 3].map(s => {
          const x = week.shifts[s] || {};
          return (
            <div key={s} style={{ ...st.row, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 90, fontSize: 14, fontWeight: 600 }}>{SHIFT[s]}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Num value={x.people_min} disabled={!canEdit} step={0.5} placeholder="—" onChange={v => setShift(s, 'people_min', v)} />
                <span style={{ color: MUTED, fontSize: 12 }}>to</span>
                <Num value={x.people_max} disabled={!canEdit} step={0.5} placeholder="—" onChange={v => setShift(s, 'people_max', v)} />
                <span style={{ color: MUTED, fontSize: 12 }}>people</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                <span style={{ color: MUTED, fontSize: 12 }}>On call</span>
                <input value={x.on_call_who || ''} disabled={!canEdit} placeholder="nobody"
                  onChange={e => setShift(s, 'on_call_who', e.target.value)} style={{ ...st.input, width: 110 }} />
                <span style={{ color: MUTED, fontSize: 12 }}>up to</span>
                <Num value={x.on_call_hours || ''} disabled={!canEdit} step={0.5} placeholder="0" width={50} onChange={v => setShift(s, 'on_call_hours', v)} />
                <span style={{ color: MUTED, fontSize: 12 }}>h</span>
              </div>
            </div>
          );
        })}
        <div style={{ ...st.row, ...st.hint }}>
          Leave a shift empty and it is not checked. Someone <b style={{ color: DIM }}>on call</b> is not counted as capacity: when a shift only works with them, the board says it needs them to come in.
        </div>
        <div style={{ ...st.row, ...st.hint }}>
          On a day with a <b style={{ color: DIM }}>shift window</b>, nobody extra comes: when the afternoon usually has nobody, the morning's people split with it, for every machine that day. Two people become one in the morning and one in the afternoon; the morning keeps any odd one. A 3-shift window brings its own people for the nights.
        </div>

        <div style={{ ...st.row, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={st.label}>Mould changes a day</div>
            <div style={st.hint}>How many changeovers the crew and the forklift can manage in one day. A changeover's minutes also count as attention.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button disabled={!canEdit} style={{ ...st.ghost, width: 30 }} onClick={() => { setWeek(w => ({ ...w, mould_changes_per_day: Math.max(1, w.mould_changes_per_day - 1) })); setWeekState(null); }}>−</button>
            <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 700, color: TEAL }}>{week.mould_changes_per_day}</span>
            <button disabled={!canEdit} style={{ ...st.ghost, width: 30 }} onClick={() => { setWeek(w => ({ ...w, mould_changes_per_day: w.mould_changes_per_day + 1 })); setWeekState(null); }}>+</button>
          </div>
        </div>

        <div style={st.row}>
          <div style={st.label}>When it is borderline</div>
          <div style={{ ...st.hint, marginBottom: 10 }}>Over-booking costs a hard day; under-booking turns away work you could have done. Which hurts more is your call.</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {LEANS.map(([k, label, says]) => (
              <button key={k} disabled={!canEdit} title={says} onClick={() => { setWeek(w => ({ ...w, lean: k })); setWeekState(null); }}
                style={{ padding: '7px 14px', fontSize: 13, fontWeight: 600, border: `1px solid ${week.lean === k ? TEAL : BORD}`, borderRadius: 8, background: week.lean === k ? 'rgba(20,184,166,0.12)' : SUNK, color: week.lean === k ? TEAL : DIM, cursor: canEdit ? 'pointer' : 'default', fontFamily: 'system-ui' }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ ...st.hint, marginTop: 8 }}>{(LEANS.find(l => l[0] === week.lean) || [])[2]}.</div>
        </div>

        {canEdit && (
          <div style={{ padding: '12px 18px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
            {weekState && weekState !== 'saving' && <span style={{ fontSize: 12, color: weekState === 'saved' ? TEAL : RED }}>{weekState === 'saved' ? 'Saved' : weekState}</span>}
            <button style={st.btn} onClick={saveWeek} disabled={weekState === 'saving'}>{weekState === 'saving' ? 'Saving…' : 'Save the usual week'}</button>
          </div>
        )}
      </div>

      {/* ── days that differ ── */}
      <div style={st.card}>
        <div style={st.head}>
          <span style={st.eyebrow}><i className="fa-solid fa-calendar-day" style={{ marginRight: 6 }} />Days that differ</span>
          <span style={{ fontSize: 11.5, color: MUTED }}>extra people, or someone away — also on the board's + Staff</span>
        </div>
        {/* The same editor, over the same rows, as the board's "+ Staff" pop-up. */}
        <ExtraStaffEditor canEdit={canEdit} />
      </div>

      {/* ── attention per part ── */}
      <div style={st.card} id="attention">
        <div style={st.head}>
          <span style={st.eyebrow}><i className="fa-solid fa-hand" style={{ marginRight: 6 }} />Attention per part</span>
          <span style={{ fontSize: 11.5, color: unset.length ? AMBER : MUTED }}>{unset.length ? `${unset.length} not yet estimated` : 'all estimated'}</span>
        </div>
        <div style={{ ...st.row, ...st.hint }}>
          How much of a person a part needs while it runs — a property of the job, not of who minds it. Levels 1 to 4 are spread across the shift and add up: a collector box to swap every hour, the hopper every few. A 5 keeps someone at the machine, a minute away at most, so it takes a whole person; if a part can be left for longer, it is a 4.
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {Object.entries(lvl).map(([l, v]) => (
              <span key={l} style={{ fontSize: 11.5, color: DIM, background: SUNK, border: `1px solid ${BORD}`, borderRadius: 6, padding: '3px 8px' }}>
                <b style={{ color: TEXT }}>{l}</b> {v.words} · {v.person ? 'a whole person' : `${v.hours[0]}–${v.hours[1]} h`}
              </span>
            ))}
          </div>
        </div>
        <div style={{ ...st.row, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input value={filter} placeholder="Find a part…" onChange={e => setFilter(e.target.value)} style={{ ...st.input, flex: 1, minWidth: 160 }} />
          {canEdit && suggestible.length > 0 && (
            <button style={st.ghost} onClick={acceptSuggestions} title="Fills every part not yet estimated that has a suggestion. Nothing is saved until you press Save.">
              Use the suggestions for the {suggestible.length} not yet estimated
            </button>
          )}
        </div>
        <div style={{ maxHeight: 520, overflowY: 'auto' }}>
          {parts.map(p => {
            const cur = levels[p.id] ?? '';
            const col = collectors[p.id] ?? '';
            const differs = levelDiffers(p) || collectorDiffers(p);
            return (
              <div key={p.id} style={{ padding: '10px 18px', borderBottom: `1px solid ${BORD}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.name} {differs && <span style={{ color: AMBER, fontSize: 11 }}>· changed</span>}
                  </div>
                  <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.45 }}>
                    {p.suggestion.level != null
                      ? <>Suggested <b style={{ color: DIM }}>{p.suggestion.level}</b> — {p.suggestion.reason}</>
                      : <>No suggestion — {p.suggestion.reason}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
                  title="Collector box at the machine: the pieces the box the parts drop into holds. Not the final pack.">
                  <input type="number" min="1" value={col} disabled={!canEdit} placeholder="collector"
                    onChange={e => { const v = e.target.value; setCollectors(c => ({ ...c, [p.id]: v })); setLevelState(null); }}
                    style={{ ...st.input, width: 92, textAlign: 'right' }} />
                  <span style={{ fontSize: 11, color: MUTED }}>pcs</span>
                </div>
                <select value={cur} disabled={!canEdit} onChange={e => { const v = e.target.value; setLevels(l => ({ ...l, [p.id]: v === '' ? '' : Number(v) })); setLevelState(null); }}
                  style={{ ...st.input, width: 150, color: cur === '' ? AMBER : TEXT }}>
                  <option value="">Not estimated</option>
                  {Object.entries(lvl).map(([l, v]) => <option key={l} value={l}>{l} · {v.person ? 'a whole person' : v.words}</option>)}
                </select>
              </div>
            );
          })}
        </div>
        {canEdit && (
          <div style={{ padding: '12px 18px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
            {levelState && levelState !== 'saving' && <span style={{ fontSize: 12, color: levelState.startsWith('Saved') ? TEAL : RED }}>{levelState}</span>}
            <button style={st.btn} onClick={saveLevels} disabled={!changedLevels.length || levelState === 'saving'}>
              {levelState === 'saving' ? 'Saving…' : changedLevels.length ? `Save ${changedLevels.length} part${changedLevels.length === 1 ? '' : 's'}` : 'Nothing to save'}
            </button>
          </div>
        )}
      </div>
    </Page>
  );
}

function Page({ goBack, children }) {
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: 11 }} /> Back
        </button>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Staffing</h1>
        <p style={{ color: MUTED, fontSize: 14, marginBottom: 24 }}>People available for moulding, and how much of a person each part needs</p>
        {children}
      </div>
    </div>
  );
}
